import React, { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useNavigate, useLocation } from "react-router-dom";
import "./StripeCheckoutPage.css";

export default function CheckoutPage() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();

  const parsedIntent = (() => {
    try {
      return (
        location.state?.paymentIntent ||
        JSON.parse(sessionStorage.getItem("paymentIntent") || "null")
      );
    } catch {
      return null;
    }
  })();

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [loadingOverlay, setLoadingOverlay] = useState(true);

  useEffect(() => {
    if (!parsedIntent) {
      navigate("/home/guest", { replace: true });
    }
  }, [parsedIntent, navigate]);

  if (!parsedIntent) {
    return <p className="stripe-checkout-error">Missing payment details</p>;
  }

  const { id: paymentIntentId, amount, metadata, receipt_email } = parsedIntent;
  const guestName = metadata?.guestName || "Guest User";
  const guestEmail = metadata?.guestEmail || receipt_email;
  const paymentFor = metadata?.paymentFor || "N/A";
  const paymentReference = metadata?.paymentReference || "N/A";

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setConfirmLoading(true);
    setLoadingOverlay(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-result?eventId=${metadata.eventId}`,
        },
      });

      if (error) {
        setMessage(error.message);
        setStatus("failed");
      } else if (paymentIntent?.status === "succeeded") {
        setStatus("succeeded");
        setMessage("Payment Successful 🎉");
        sessionStorage.removeItem("paymentIntent");
      } else {
        setStatus(paymentIntent?.status || "processing");
      }
    } catch (err) {
      setMessage(err.message || "Payment failed");
      setStatus("failed");
    } finally {
      setConfirmLoading(false);
      setLoadingOverlay(false);
    }
  };

  return (
    <div className="stripe-checkout-container">
      <div className="stripe-checkout-card">
        <h2 className="stripe-checkout-title">
          {status === "succeeded"
            ? "Payment Successful 🎉"
            : "Confirm Your Payment"}
        </h2>

        <div className="stripe-checkout-summary">
          <p>
            <strong>Name:</strong> {guestName}
          </p>
          <p>
            <strong>Email:</strong> {guestEmail}
          </p>
          <p>
            <strong>Payment For:</strong> {paymentFor}
          </p>
          <p>
            <strong>Reference:</strong> {paymentReference}
          </p>
          <p>
            <strong>Payment Intent Id:</strong> {paymentIntentId}
          </p>
        </div>

        {status !== "succeeded" && (
          <div className="stripe-checkout-payment-box">
            <PaymentElement
              onReady={() => {
                setPaymentReady(true);
                setLoadingOverlay(false);
              }}
            />
          </div>
        )}

        <button
          className="stripe-checkout-pay-btn"
          disabled={
            !stripe ||
            !elements ||
            !paymentReady ||
            confirmLoading ||
            status === "succeeded"
          }
          onClick={handleSubmit}
        >
          {confirmLoading
            ? "Processing..."
            : status === "succeeded"
            ? "Payment Successful"
            : `Pay $${(amount / 100).toFixed(2)}`}
        </button>

        {message && <p className="stripe-checkout-message">{message}</p>}
      </div>

      {loadingOverlay && (
        <div className="stripe-loading-overlay">
          <div className="stripe-loading-spinner"></div>
        </div>
      )}
    </div>
  );
}
